import { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Search, Pin, Star, Menu, Plus,
  MoreHorizontal, Archive, Trash, Share2, FileDown, Undo2, Copy, Globe, BookOpen
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { exportNoteToPDF } from '@/lib/pdfExport';

export default function NoteList() {
  const {
    filteredNotes, selectedNoteId, setSelectedNoteId,
    searchQuery, setSearchQuery,
    activeView, filterNotebookId, filterTagId,
    notebooks, tags, setSidebarOpen, createNote,
    togglePin, toggleFavorite, archiveNote, deleteNote,
    restoreNote, permanentDeleteNote, shareNote,
    setPublishDialogRequested,
    includeJournalInAll, setIncludeJournalInAll,
    emptyTrash,
  } = useApp();
  const [openMenuId, setOpenMenuId] = useState(null);

  const handleDuplicate = async (note) => {
    await createNote({
      title: `${note.title || 'Untitled'} (copy)`,
      content: note.content || '',
      tags: note.tags || [],
      notebook_id: note.notebook_id || null,
    });
  };

  const getViewTitle = () => {
    if (filterNotebookId) {
      const nb = notebooks.find((n) => n.id === filterNotebookId);
      return nb ? nb.name : 'Notebook';
    }
    if (filterTagId) {
      const tag = tags.find((t) => t.id === filterTagId);
      return tag ? `#${tag.name}` : 'Tag';
    }
    switch (activeView) {
      case 'favorites': return 'Favorites';
      case 'archived': return 'Archive';
      case 'trash': return 'Trash';
      default: return 'All Notes';
    }
  };

  const truncateContent = (content, maxLen = 80) => {
    if (!content) return '';
    // Strip HTML tags, decode entities, then clean whitespace
    const plain = content
      .replace(/<[^>]*>/g, ' ')
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
      .replace(/<[^>]*>/g, ' ')
      .replace(/[#*`\[\]_~>|\\-]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    return plain.length > maxLen ? plain.slice(0, maxLen) + '...' : plain;
  };

  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const formatCreatedDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch {
      return '';
    }
  };

  const handleNewNote = async () => {
    const data = {};
    if (filterNotebookId) {
      data.notebook_id = filterNotebookId;
    }
    await createNote(data);
  };

  return (
    <div className="flex flex-col h-full w-full" data-testid="note-list-panel">
      {/* Header */}
      <div className="p-3 sm:p-3 border-b border-border/50">
        {/* Top row: hamburger + title + new note */}
        <div className="flex items-center gap-2 mb-2.5">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-sm text-muted-foreground hover:text-foreground active:bg-muted/50 transition-colors -ml-1"
            data-testid="note-list-toggle-sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h2 className="flex-1 text-base font-semibold tracking-tight" data-testid="note-list-title">
            {getViewTitle()}
          </h2>
          <span className="text-xs text-muted-foreground mr-1">{filteredNotes.length}</span>
          {activeView === 'trash' && filteredNotes.length > 0 && (
            <Button
              onClick={() => {
                if (window.confirm(`Permanently delete all ${filteredNotes.length} notes in trash?`)) {
                  emptyTrash();
                }
              }}
              variant="ghost"
              className="text-xs text-red-400 hover:text-red-300 hover:bg-red-400/10 h-8 px-2.5"
              data-testid="note-list-empty-trash-btn"
            >
              <Trash className="w-3.5 h-3.5 mr-1" />
              Empty
            </Button>
          )}
          <Button
            onClick={handleNewNote}
            size="icon"
            variant="ghost"
            className="w-10 h-10 text-accent-amber hover:text-accent-amber hover:bg-accent-amber/10 -mr-1"
            data-testid="note-list-new-note-btn"
          >
            <Plus className="w-5 h-5" />
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search... tag: notebook: is:pinned"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-10 text-sm bg-secondary/50 border-border"
            data-testid="note-list-search-input"
          />
        </div>
      </div>

      {/* Journal toggle — shown only in unfiltered All Notes view */}
      {activeView === 'all' && !filterNotebookId && !filterTagId && !searchQuery && (
        <div className="px-3 py-1.5 border-b border-border/30 flex items-center">
          <button
            onClick={() => setIncludeJournalInAll(!includeJournalInAll)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              includeJournalInAll
                ? 'bg-accent-amber/15 text-accent-amber'
                : 'text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/40'
            }`}
            data-testid="note-list-journal-toggle"
          >
            <BookOpen className="w-3 h-3" />
            Include journal
          </button>
        </div>
      )}

      {/* Note Items */}
      <ScrollArea className="flex-1 w-full">
        <div className="w-full overflow-hidden">
          {filteredNotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="note-list-empty">
              <p className="text-sm text-muted-foreground">No notes found</p>
              <p className="text-xs text-muted-foreground/50 mt-1.5">
                {searchQuery ? 'Try a different search term' : 'Tap + to create a new note'}
              </p>
            </div>
          ) : (
            filteredNotes.map((note) => (
              <div
                key={note.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/vaultnotes-note', JSON.stringify({ noteId: note.id, title: note.title }));
                  e.dataTransfer.effectAllowed = 'move';
                  e.currentTarget.classList.add('opacity-50');
                }}
                onDragEnd={(e) => {
                  e.currentTarget.classList.remove('opacity-50');
                }}
                onClick={() => setSelectedNoteId(note.id)}
                data-testid={`note-item-${note.id}`}
                className={`note-item group w-full text-left pl-4 pr-8 py-3.5 border-b border-border/30 transition-colors min-h-[72px] cursor-grab active:cursor-grabbing overflow-hidden relative ${
                  selectedNoteId === note.id
                    ? 'bg-muted/80'
                    : 'hover:bg-muted/30 active:bg-muted/50'
                }`}
              >
                <div className="min-w-0">
                    <div className="flex items-center gap-1.5 pr-6">
                      {note.is_pinned && <Pin className="w-3.5 h-3.5 text-accent-amber flex-shrink-0 fill-current" />}
                      {note.is_favorited && <Star className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0 fill-current" />}
                      <h3 className="text-sm font-medium truncate flex-1 min-w-0">
                        {note.title || 'Untitled'}
                      </h3>
                    </div>
                      {/* Three-dot menu — absolutely positioned */}
                      <div className="absolute top-3 right-2">
                      <DropdownMenu open={openMenuId === note.id} onOpenChange={(open) => setOpenMenuId(open ? note.id : null)}>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className={`p-1 rounded-sm transition-colors flex-shrink-0 ${
                              openMenuId === note.id
                                ? 'text-foreground bg-muted'
                                : 'text-foreground/50 hover:text-foreground'
                            }`}
                            data-testid={`note-menu-${note.id}`}
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => { togglePin(note.id); setOpenMenuId(null); }} className="min-h-[36px]" data-testid={`note-menu-pin-${note.id}`}>
                            <Pin className={`w-3.5 h-3.5 mr-2 ${note.is_pinned ? 'fill-current text-accent-amber' : ''}`} />
                            {note.is_pinned ? 'Unpin' : 'Pin'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { toggleFavorite(note.id); setOpenMenuId(null); }} className="min-h-[36px]" data-testid={`note-menu-fav-${note.id}`}>
                            <Star className={`w-3.5 h-3.5 mr-2 ${note.is_favorited ? 'fill-current text-yellow-500' : ''}`} />
                            {note.is_favorited ? 'Unfavorite' : 'Favorite'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => { exportNoteToPDF(note); setOpenMenuId(null); }} className="min-h-[36px]" data-testid={`note-menu-pdf-${note.id}`}>
                            <FileDown className="w-3.5 h-3.5 mr-2" /> Export PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { handleDuplicate(note); setOpenMenuId(null); }} className="min-h-[36px]" data-testid={`note-menu-duplicate-${note.id}`}>
                            <Copy className="w-3.5 h-3.5 mr-2" /> Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelectedNoteId(note.id); setPublishDialogRequested(true); setOpenMenuId(null); }} className="min-h-[36px]" data-testid={`note-menu-publish-${note.id}`}>
                            <Globe className="w-3.5 h-3.5 mr-2" /> {note.is_published ? 'Publish Settings' : 'Publish'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {activeView === 'trash' ? (
                            <>
                              <DropdownMenuItem onClick={() => { restoreNote(note.id); setOpenMenuId(null); }} className="min-h-[36px]" data-testid={`note-menu-restore-${note.id}`}>
                                <Undo2 className="w-3.5 h-3.5 mr-2" /> Restore
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { permanentDeleteNote(note.id); setOpenMenuId(null); }} className="min-h-[36px] text-red-400" data-testid={`note-menu-permdelete-${note.id}`}>
                                <Trash className="w-3.5 h-3.5 mr-2" /> Delete Forever
                              </DropdownMenuItem>
                            </>
                          ) : activeView === 'archived' ? (
                            <DropdownMenuItem onClick={() => { restoreNote(note.id); setOpenMenuId(null); }} className="min-h-[36px]" data-testid={`note-menu-restore-${note.id}`}>
                              <Undo2 className="w-3.5 h-3.5 mr-2" /> Restore
                            </DropdownMenuItem>
                          ) : (
                            <>
                              <DropdownMenuItem onClick={() => { archiveNote(note.id); setOpenMenuId(null); }} className="min-h-[36px]" data-testid={`note-menu-archive-${note.id}`}>
                                <Archive className="w-3.5 h-3.5 mr-2" /> Archive
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { deleteNote(note.id); setOpenMenuId(null); }} className="min-h-[36px] text-red-400" data-testid={`note-menu-delete-${note.id}`}>
                                <Trash className="w-3.5 h-3.5 mr-2" /> Move to Trash
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed break-words">
                      {(note._decryptFailed || note._needsDecryption) ? '[Encrypted - unlock to view]' : truncateContent(note.content)}
                    </p>
                    {/* Notebook + Tags */}
                    {(
                      (activeView === 'all' && !filterNotebookId && note.notebook_id) ||
                      (note.tags && note.tags.length > 0)
                    ) && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {activeView === 'all' && !filterNotebookId && note.notebook_id && (() => {
                          const nb = notebooks.find((n) => n.id === note.notebook_id);
                          return nb ? (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full bg-accent-amber/15 text-accent-amber"
                              data-testid={`note-notebook-badge-${note.id}`}
                            >
                              {nb.name}
                            </span>
                          ) : null;
                        })()}
                        {note.tags && note.tags.slice(0, 4).map((tagId) => {
                          const tag = tags.find((t) => t.id === tagId);
                          return tag ? (
                            <span
                              key={tagId}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full"
                              style={{ 
                                backgroundColor: tag.color + '20', 
                                color: tag.color 
                              }}
                              data-testid={`note-tag-${tagId}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tag.color }} />
                              {tag.name}
                            </span>
                          ) : null;
                        })}
                        {note.tags && note.tags.length > 4 && (
                          <span className="text-[10px] text-muted-foreground">+{note.tags.length - 4}</span>
                        )}
                      </div>
                    )}
                    {/* Date info */}
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] text-muted-foreground/50">{formatCreatedDate(note.created_at)}</span>
                      <span className="text-[10px] text-muted-foreground/30">·</span>
                      <span className="text-[10px] text-muted-foreground/50">{formatTime(note.updated_at)}</span>
                      {note.is_journal && (
                        <>
                          <span className="text-[10px] text-muted-foreground/30">·</span>
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-accent-amber/70" data-testid={`note-journal-indicator-${note.id}`}>
                            <BookOpen className="w-2.5 h-2.5" />
                            Journal
                          </span>
                        </>
                      )}
                    </div>
                  </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
